import React, { useState, useRef } from 'react'
import {Upload, X, Image as ImageIcon, Loader} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface BlogImageUploadProps {
  currentImageUrl?: string
  onImageUploaded: (imageUrl: string) => void
  onImageRemoved?: () => void
}

export default function BlogImageUpload({
  currentImageUrl,
  onImageUploaded,
  onImageRemoved
}: BlogImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const file = files[0]

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, or WebP images.')
      return
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      setError('File too large. Maximum size is 10MB.')
      return
    }

    setError(null)
    setUploading(true)

    try {
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to Lumi
      const results = await lumi.tools.file.upload([file])

      if (results[0]?.fileUrl) {
        onImageUploaded(results[0].fileUrl)
        setPreviewUrl(results[0].fileUrl)
        setError(null)
      } else if (results[0]?.uploadError) {
        setError(results[0].uploadError)
        setPreviewUrl(null)
      }
    } catch (err) {
      console.error('Upload error:', err)
      setError('Failed to upload image. Please try again.')
      setPreviewUrl(null)
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async () => {
    if (previewUrl && previewUrl.startsWith('http')) {
      try {
        await lumi.tools.file.delete([previewUrl])
      } catch (err) {
        console.error('Delete error:', err)
      }
    }
    setPreviewUrl(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    if (onImageRemoved) {
      onImageRemoved()
    }
  }

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Featured Image
      </label>

      {/* Preview or Upload Area */}
      {previewUrl ? (
        <div className="relative w-full h-64 rounded-lg overflow-hidden border-2 border-gray-300 bg-gray-50">
          <img
            src={previewUrl}
            alt="Featured preview"
            className="w-full h-full object-cover"
          />
          <button
            onClick={handleRemove}
            disabled={uploading}
            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg disabled:opacity-50"
            title="Remove image"
          >
            <X size={20} />
          </button>
          {uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <Loader className="animate-spin text-white" size={40} />
            </div>
          )}
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-full h-64 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors bg-gray-50"
        >
          {uploading ? (
            <>
              <Loader className="animate-spin text-blue-500 mb-3" size={48} />
              <p className="text-gray-600 font-medium">Uploading image...</p>
            </>
          ) : (
            <>
              <Upload className="text-gray-400 mb-3" size={48} />
              <p className="text-gray-600 font-medium mb-1">Click to upload featured image</p>
              <p className="text-gray-500 text-sm">JPG, PNG, or WebP (max 10MB)</p>
            </>
          )}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Error Message */}
      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <X className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Help Text */}
      {!error && (
        <p className="mt-2 text-xs text-gray-500">
          Recommended size: 1200x630px for optimal display across all devices
        </p>
      )}
    </div>
  )
}
